import { useEffect, useState } from 'react';
import { useLang } from '../i18n/context';
import SEO from '../components/SEO';
import { Container, Reveal } from '../components/Reveal';
import { WhatsAppButton, EmailButton, WaIcon } from '../components/CTAs';
import { breadcrumbSchema } from '../lib/schema';
import { submitEnquiry } from '../lib/api';
import { captureUtms, track, getUtms } from '../lib/track';
import { SITE } from '../lib/site';
import { toast } from 'sonner';

const inputCls = 'w-full rounded-xl border border-peri-300 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-subtle focus:border-gold-600 focus:outline-none focus:ring-2 focus:ring-gold-600/20';

export default function Contact() {
    const { t, lang } = useLang();
    const f = t.contact.form;
    const [form, setForm] = useState({ name: '', contact_method: f.methodOptions[0], treatment_interest: f.treatmentOptions[4], phone_code: '+60', phone: '', preferred_date: '', preferred_time: f.timeOptions[0], country: f.countryOptions[0], message: '' });
    const [sending, setSending] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        captureUtms();
        track('appointment_form_open', { source: 'contact-page' });
        track('aira_appointment_started', { source: 'dental_form', page: window.location.pathname });
        const sessionId = sessionStorage.getItem('aira_session');
        if (sessionId) {
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/state/${sessionId}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((s) => {
                    if (!s) return;
                    setForm((prev) => ({
                        ...prev,
                        name: prev.name || s.name || '',
                        phone: prev.phone || (s.phone || '').replace(/^\+60/, '').replace(/^\+/, ''),
                        treatment_interest: s.treatment_interest && f.treatmentOptions.includes(s.treatment_interest) ? s.treatment_interest : prev.treatment_interest,
                        preferred_date: prev.preferred_date || s.preferred_date || '',
                        message: prev.message || s.patient_concern || '',
                    }));
                })
                .catch(() => {});
        }
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            let sessionId = sessionStorage.getItem('aira_session');
            if (!sessionId) {
                sessionId = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                sessionStorage.setItem('aira_session', sessionId);
            }
            await submitEnquiry({ ...form, phone: `${form.phone_code}${form.phone.replace(/\D/g, '').replace(/^0+/, '')}`, page: `/dental/${lang}/contact`, language: lang, session_id: sessionId, utm: getUtms() });
            track('appointment_form_submit', { treatment: form.treatment_interest, country: form.country });
            track('aira_appointment_request_completed', { source: 'dental_form', treatment_category: (form.treatment_interest || '').toLowerCase().replace(/[^a-z0-9]+/g, '-'), page: window.location.pathname });
            toast.success(f.success);
            setSubmitted(true);
        } catch (err) {
            toast.error(f.error);
        } finally {
            setSending(false);
        }
    };

    const waConfirmText = [
        form.name ? `Hi, saya ${form.name}.` : 'Hi!',
        'Saya baru sahaja menghantar borang temu janji di laman web.',
        '',
        'APPOINTMENT REQUEST',
        form.name ? `Nama: ${form.name}` : '',
        `Telefon: ${form.phone_code}${form.phone.replace(/\D/g, '').replace(/^0+/, '')}`,
        `Rawatan: ${form.treatment_interest}`,
        form.preferred_date ? `Tarikh pilihan: ${form.preferred_date}` : '',
        `Masa pilihan: ${form.preferred_time}`,
        form.message ? `Ringkasan: ${form.message}` : '',
        'Sumber: iINSPIRE Dental — Contact Form',
        'Mohon semak availability dan confirm appointment saya. Terima kasih.',
    ].filter(Boolean).join('\n');

    return (
        <>
            <SEO
                title={t.contact.metaTitle}
                description={t.contact.metaDesc}
                path="/contact"
                lang={lang}
                jsonLd={[breadcrumbSchema(lang, [{ name: t.nav.home, path: '/' }, { name: t.contact.title, path: '/contact' }])]}
            />
            <section className="relative overflow-hidden pt-28 md:pt-36" data-testid="contact-page">
                <div className="pointer-events-none absolute -right-32 -top-20 h-96 w-96 rounded-full bg-peri-200/70" aria-hidden="true" />
                <Container className="relative">
                    <Reveal>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold-700">{t.contact.title}</p>
                        <h1 className="mt-4 max-w-3xl font-serif text-4xl font-semibold leading-tight tracking-tight text-ink md:text-6xl">{t.contact.title}</h1>
                        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-muted">{t.contact.sub}</p>
                    </Reveal>
                </Container>
            </section>

            <section className="py-14 md:py-20">
                <Container>
                    <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr]">
                        <div className="space-y-4">
                            <Reveal>
                                <div className="rounded-3xl bg-wa p-7 text-white" data-testid="contact-whatsapp-block">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] opacity-90">{t.contact.whatsappTitle}</p>
                                    <p className="mt-3 font-serif text-3xl font-semibold">{SITE.whatsappDisplay}</p>
                                    <a
                                        href={`https://wa.me/${SITE.whatsappIntl}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-ink transition-transform hover:scale-[1.02]"
                                        data-testid="contact-whatsapp-cta"
                                    >
                                        <WaIcon className="h-4 w-4 text-wa" />
                                        {t.cta.whatsapp}
                                    </a>
                                </div>
                            </Reveal>
                            <Reveal delay={0.06}>
                                <div className="rounded-3xl border border-peri-200 bg-white p-7">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.contact.emailTitle}</p>
                                    <p className="mt-3 break-all text-[15px] font-medium text-ink">{SITE.email}</p>
                                    <EmailButton label={t.cta.email} source="contact-page" className="mt-3 text-sm font-semibold text-gold-700" testId="contact-email-cta" />
                                </div>
                            </Reveal>
                            <Reveal delay={0.12}>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="rounded-3xl border border-peri-200 bg-white p-7">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.contact.addressTitle}</p>
                                        <p className="mt-3 text-[15px] text-ink-body">{SITE.location}</p>
                                        <p className="mt-2 text-xs italic text-ink-subtle">{t.contact.addressValue}</p>
                                    </div>
                                    <div className="rounded-3xl border border-peri-200 bg-white p-7">
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.contact.hoursTitle}</p>
                                        <p className="mt-3 text-xs italic text-ink-subtle">{t.contact.hoursValue}</p>
                                    </div>
                                </div>
                            </Reveal>
                        </div>

                        <Reveal delay={0.08}>
                            {submitted ? (
                                <div className="rounded-3xl border border-peri-200 bg-white p-7 md:p-9" data-testid="contact-success-panel">
                                    <span className="inline-block rounded-full bg-gold-400/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold-700">APPOINTMENT PENDING</span>
                                    <h2 className="mt-4 font-serif text-2xl font-semibold text-ink">{f.successTitle}</h2>
                                    <p className="mt-3 text-sm leading-relaxed text-ink-muted">{f.successBody}</p>
                                    <div className="mt-5 rounded-2xl bg-peri-50 p-5 text-sm text-ink" data-testid="contact-success-summary">
                                        <p><strong>{form.name}</strong> · {form.treatment_interest}</p>
                                        <p className="mt-1 text-ink-muted">{form.phone_code}{form.phone.replace(/\D/g, '').replace(/^0+/, '')}</p>
                                        <p className="mt-1 text-ink-muted">{form.preferred_date} · {form.preferred_time}</p>
                                    </div>
                                    <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                        <button onClick={() => window.dispatchEvent(new CustomEvent('aira:open'))} data-testid="contact-success-aira" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-gold-600 px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-gold-400">
                                            {f.successAira}
                                        </button>
                                        <a href={`https://wa.me/${SITE.whatsappIntl}?text=${encodeURIComponent(waConfirmText)}`} target="_blank" rel="noopener noreferrer" data-testid="contact-success-whatsapp" className="inline-flex items-center justify-center gap-2 rounded-full bg-wa px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02]">
                                            <WaIcon className="h-4 w-4" />
                                            {f.successWa}
                                        </a>
                                    </div>
                                </div>
                            ) : (
                            <form onSubmit={onSubmit} className="rounded-3xl border border-peri-200 bg-white p-7 md:p-9" data-testid="contact-form">
                                <h2 className="font-serif text-2xl font-semibold text-ink">{t.contact.formTitle}</h2>
                                <div className="mt-6 space-y-4">
                                    <div>
                                        <label className="mb-1.5 block text-sm font-semibold text-ink" htmlFor="cf-name">{f.name}</label>
                                        <input id="cf-name" data-testid="contact-name-input" required maxLength={120} value={form.name} onChange={set('name')} placeholder={f.namePh} className={inputCls} />
                                    </div>
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="mb-1.5 block text-sm font-semibold text-ink" htmlFor="cf-method">{f.method}</label>
                                            <select id="cf-method" data-testid="contact-method-select" value={form.contact_method} onChange={set('contact_method')} className={inputCls}>
                                                {f.methodOptions.map((o) => <option key={o}>{o}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="mb-1.5 block text-sm font-semibold text-ink" htmlFor="cf-phone">{f.phoneLabel}</label>
                                            <div className="flex gap-2">
                                                <select aria-label="Country code" data-testid="contact-phone-code" value={form.phone_code} onChange={set('phone_code')} className={`${inputCls} !w-28 shrink-0`}>
                                                    <option value="+60">🇲🇾 +60</option>
                                                    <option value="+65">🇸🇬 +65</option>
                                                    <option value="+62">🇮🇩 +62</option>
                                                    <option value="+63">🇵🇭 +63</option>
                                                    <option value="+91">🇮🇳 +91</option>
                                                    <option value="+86">🇨🇳 +86</option>
                                                    <option value="+44">🇬🇧 +44</option>
                                                    <option value="+1">🇺🇸 +1</option>
                                                </select>
                                                <input id="cf-phone" type="tel" inputMode="tel" required data-testid="contact-phone-input" value={form.phone} onChange={set('phone')} placeholder={f.phonePh} className={inputCls} />
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1.5 block text-sm font-semibold text-ink" htmlFor="cf-treatment">{f.treatment}</label>
                                        <select id="cf-treatment" data-testid="contact-treatment-select" value={form.treatment_interest} onChange={set('treatment_interest')} className={inputCls}>
                                            {f.treatmentOptions.map((o) => <option key={o}>{o}</option>)}
                                        </select>
                                    </div>
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="mb-1.5 block text-sm font-semibold text-ink" htmlFor="cf-date">{f.dateLabel}</label>
                                            <input id="cf-date" type="date" required data-testid="contact-date-input" value={form.preferred_date} onChange={set('preferred_date')} className={inputCls} />
                                        </div>
                                        <div>
                                            <label className="mb-1.5 block text-sm font-semibold text-ink" htmlFor="cf-time">{f.timeLabel}</label>
                                            <select id="cf-time" data-testid="contact-time-input" value={form.preferred_time} onChange={set('preferred_time')} className={inputCls}>
                                                {f.timeOptions.map((o) => <option key={o}>{o}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1.5 block text-sm font-semibold text-ink" htmlFor="cf-country">{f.country}</label>
                                        <select id="cf-country" data-testid="contact-country-select" value={form.country} onChange={set('country')} className={inputCls}>
                                            {f.countryOptions.map((o) => <option key={o}>{o}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="mb-1.5 block text-sm font-semibold text-ink" htmlFor="cf-message">{f.message}</label>
                                        <textarea id="cf-message" data-testid="contact-message-input" rows={4} maxLength={2000} value={form.message} onChange={set('message')} placeholder={f.messagePh} className={inputCls} />
                                    </div>
                                    <p className="text-xs leading-relaxed text-ink-subtle">{f.consent}</p>
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        data-testid="contact-submit"
                                        className="w-full rounded-full bg-gold-400 px-6 py-3.5 text-sm font-semibold text-ink transition-all hover:bg-gold-500 disabled:opacity-60 sm:w-auto"
                                    >
                                        {sending ? '…' : f.submit}
                                    </button>
                                </div>
                            </form>
                            )}
                        </Reveal>
                    </div>
                </Container>
            </section>
        </>
    );
}
